import React, { useState, useEffect } from 'react';
import { Drawer, Form, Input, InputNumber, Select, Button, message, Spin } from 'antd';
import { PlusOutlined, WalletOutlined } from '@ant-design/icons';
import { incomeService, SourceWallet } from '../../services/incomeService';
import styles from '../common/DrawerModalMobile.module.css';

interface AddWalletModalMobileProps {
  visible: boolean;
  onClose: () => void;
  onSuccess: () => void;
  sources: any[];
  editingWallet?: SourceWallet | null;
}

const AddWalletModalMobile: React.FC<AddWalletModalMobileProps> = ({
  visible,
  onClose,
  onSuccess,
  sources,
  editingWallet
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (visible) {
      if (editingWallet) {
        // Chế độ chỉnh sửa - điền dữ liệu hiện tại
        form.setFieldsValue({
          name: editingWallet.name,
          source_id: editingWallet.source_id,
          initial_balance: editingWallet.initial_balance,
          description: editingWallet.description || ''
        });
      } else {
        // Chế độ thêm mới - reset form
        form.resetFields();
      }
    }
  }, [visible, form, editingWallet]);

  const handleSubmit = async (values: any) => {
    try {
      setSubmitting(true);
      
      if (editingWallet) {
        // Chế độ chỉnh sửa
        await incomeService.updateWallet(editingWallet.id, {
          name: values.name,
          description: values.description || ''
        });
        message.success('Cập nhật ví thành công!');
      } else {
        // Chế độ thêm mới
        await incomeService.createWallet({
          name: values.name,
          source_id: values.source_id,
          initial_balance: values.initial_balance || 0,
          description: values.description || ''
        });
        message.success('Tạo ví thành công!');
      }
      
      form.resetFields();
      onSuccess();
      onClose();
    } catch (error: any) {
      message.error(error.message || 'Có lỗi xảy ra khi tạo ví');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  return (
    <Drawer
      title={editingWallet ? "Chỉnh sửa ví" : "Thêm ví mới"}
      placement="bottom"
      height="90%"
      open={visible}
      onClose={handleCancel}
      className={styles.drawer}
      styles={{
        body: { padding: 0 }
      }}
    >
      <div className={styles.drawerContent}>
        <div className={styles.drawerBody}>
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            initialValues={{
              initial_balance: 0
            }}
          >
            <Form.Item
              name="name"
              label="Tên ví"
              className={styles.formItem}
              rules={[
                { required: true, message: 'Vui lòng nhập tên ví' },
                { max: 100, message: 'Tên ví không được quá 100 ký tự' }
              ]}
            >
              <Input 
                placeholder="Nhập tên ví"
                prefix={<WalletOutlined style={{ color: '#10B981' }} />}
              />
            </Form.Item>

            <Form.Item
              name="source_id"
              label="Nguồn thu nhập"
              className={styles.formItem}
              rules={[
                { required: true, message: 'Vui lòng chọn nguồn thu nhập' }
              ]}
              hidden={!!editingWallet}
            >
              <Select
                placeholder="Chọn nguồn thu nhập"
                loading={loading}
                showSearch
                className={styles.select}
                filterOption={(input, option) =>
                  (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
                }
              >
                {sources.map(source => (
                  <Select.Option key={source.id} value={source.id}>
                    {source.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              name="initial_balance"
              label="Số dư ban đầu"
              className={styles.formItem}
              hidden={!!editingWallet}
            >
              <InputNumber
                placeholder="Nhập số dư ban đầu"
                className={styles.inputNumber}
                min={0}
              />
            </Form.Item>

            <Form.Item
              name="description"
              label="Mô tả"
              className={styles.formItem}
            >
              <Input.TextArea
                placeholder="Nhập mô tả (tùy chọn)"
                rows={3}
                maxLength={500}
                showCount
                className={styles.textArea}
              />
            </Form.Item>
          </Form>
        </div>

        <div className={styles.drawerFooter}>
          <div className={styles.drawerFooterRow}>
            <Button
              type="default"
              onClick={handleCancel}
              disabled={submitting}
              className={`${styles.drawerFooterButton} ${styles.default}`}
            >
              Hủy
            </Button>
                          <Button
                type="primary"
                htmlType="submit"
                loading={submitting}
                onClick={() => form.submit()}
                className={`${styles.drawerFooterButton} ${styles.primary}`}
                icon={<PlusOutlined />}
              >
                {editingWallet ? 'Cập nhật' : 'Tạo ví'}
              </Button>
          </div>
        </div>
      </div>
    </Drawer>
  );
};

export default AddWalletModalMobile; 